import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageContext";

// White Elephant Moments — a magazine-style vertical stack of two cinematic
// art photographs. Sits between BrandHighlights and WhiteElephantTriptych on
// the White Elephant brand page only. Each photo alternates its caption side
// (left / right) to give a print-editorial rhythm.
// -----------------------------------------------------------------------------
const IVORY = "#F6EFE0";
const CHARCOAL = "#0F1A12";
const RED = "#B02224";
const GOLD = "#C9962D";

const MOMENTS = [
  {
    key: "bitter",
    image: "/white-elephant/art/we-art-1-1.jpg",
    accent: "#4E8A3C",
    align: "right", // caption on right
  },
  {
    key: "tonics",
    image: "/white-elephant/art/we-art-2-1.jpg",
    accent: "#C9962D",
    align: "left", // caption on left
  },
];

const FALLBACK = {
  bitter: {
    title: "The bright edge.",
    body:
      "Bitter Lemon among Mediterranean citrus — real lemon juice, natural quinine, and the crisp edge that lifts a highball or a G&T.",
    tag: "Bitter Lemon",
  },
  tonics: {
    title: "The premium pour.",
    body:
      "Japanese Yuzu for citrus finesse. Barbados Breeze for tropical warmth. Two tonics, one bar-grade craft — poured over ice with a rosemary sprig.",
    tag: "Japanese Yuzu · Barbados Breeze",
  },
};

function MomentBlock({ moment, index }) {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const fb = FALLBACK[moment.key];
  const titleRaw = t(`brandContent.white-elephant.moments.${moment.key}.title`);
  const bodyRaw = t(`brandContent.white-elephant.moments.${moment.key}.body`);
  const tagRaw = t(`brandContent.white-elephant.moments.${moment.key}.tag`);
  const title = isTranslated(titleRaw) ? titleRaw : fb.title;
  const body = isTranslated(bodyRaw) ? bodyRaw : fb.body;
  const tag = isTranslated(tagRaw) ? tagRaw : fb.tag;

  const captionFirst = moment.align === "left";

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: index * 0.1 }}
      className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center"
      data-testid={`we-moment-${moment.key}`}
    >
      {/* Caption column */}
      <div className={`lg:col-span-4 ${captionFirst ? "order-2 lg:order-1" : "order-2 lg:order-2"}`}>
        <p
          className="text-[0.65rem] md:text-xs font-bold uppercase mb-4"
          style={{ color: moment.accent, letterSpacing: "0.32em" }}
        >
          {tag}
        </p>
        <h3
          className="font-editorial italic leading-[1.05] text-[clamp(1.9rem,3.4vw,3rem)] mb-5"
          style={{ color: CHARCOAL }}
        >
          {title}
        </h3>
        <div
          className="h-px w-20 mb-5"
          style={{ background: `linear-gradient(90deg, ${moment.accent}, transparent)` }}
        />
        <p
          className="text-base md:text-lg leading-[1.75] max-w-md"
          style={{ color: `${CHARCOAL}cc` }}
        >
          {body}
        </p>
      </div>

      {/* Photo column */}
      <div className={`lg:col-span-8 ${captionFirst ? "order-1 lg:order-2" : "order-1 lg:order-1"}`}>
        <div
          className="overflow-hidden rounded-3xl shadow-[0_30px_80px_-25px_rgba(15,26,18,0.4)]"
          style={{ border: `1px solid ${GOLD}33` }}
        >
          <img
            src={moment.image}
            alt={title}
            className="w-full h-auto block"
          />
        </div>
      </div>
    </motion.div>
  );
}

export default function WhiteElephantMoments() {
  const { t } = useT();
  const isTranslated = (v) => typeof v === "string" && !v.startsWith("brandContent.");
  const overlineRaw = t("brandContent.white-elephant.moments.overline");
  const overline = isTranslated(overlineRaw)
    ? overlineRaw
    : "White Elephant moments";
  const titleRaw = t("brandContent.white-elephant.moments.title");
  const title = isTranslated(titleRaw)
    ? titleRaw
    : "Where refinement meets the pour.";

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: IVORY, color: CHARCOAL }}
      data-testid="we-moments"
    >
      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
        {/* Section header */}
        <div className="max-w-2xl mb-14">
          <p
            className="text-[0.7rem] md:text-xs font-bold uppercase mb-4"
            style={{ color: RED, letterSpacing: "0.36em" }}
          >
            {overline}
          </p>
          <h2
            className="font-editorial italic leading-[1.05] text-[clamp(2.25rem,4.4vw,3.75rem)]"
            style={{ color: CHARCOAL }}
          >
            {title}
          </h2>
          <div
            className="mt-6 h-px w-24"
            style={{ background: `linear-gradient(90deg, ${GOLD}, transparent)` }}
          />
        </div>

        <div className="space-y-16 lg:space-y-24">
          {MOMENTS.map((m, i) => (
            <MomentBlock key={m.key} moment={m} index={i} />
          ))}
        </div>
      </div>

      {/* Fine bottom divider */}
      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
